import { generatePath, Navigate, useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import Loading from 'components/Loading'
import Tabs, { getPage, Tab } from 'components/Tabs'
import { ConfirmationDialog, useConfirmationDialog } from 'features/ab-tests'
import { AbTest } from 'features/ab-tests/types/abTest'
import { Alert } from 'features/alerts/types/alert'
import { DomainAlerts } from 'features/inventory/domain'
import { DomainAlert } from 'features/inventory/domain/types/DomainAlert'
import {
  AddEditPageContext,
  AddEditPageContextType,
} from 'features/inventory/page/contexts/AddEditPageContext'
import { useGetPage } from 'features/inventory/page/forms/GeneralForm/hooks/useGetPage'
import { useAbility } from 'providers/casl'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'
import { ProductTab } from 'types/productTab'
import { useGetFreshPageDomainAlerts } from '../../forms/GeneralForm/hooks/useGetFreshPageDomainAlerts'
import { EditPageUrlParams } from './types'
import { useProductTabs } from './useProductTabs'

export const EditPage = (): React.JSX.Element => {
  const ability = useAbility()
  const { id: urlPageId, tab: urlCurrentTab, workspaceId } = useParams<EditPageUrlParams>()
  const tabs: Tab<ProductTab>[] = useProductTabs()
  const { page } = useGetPage({ id: urlPageId })
  const freshPageDomainAlerts: Alert<DomainAlert>[] = useGetFreshPageDomainAlerts({
    pageId: urlPageId,
  })
  const { confirmationHandler, isDialogOpen, setIsDialogOpen } = useConfirmationDialog()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: `page-form-edit`,
  })

  if (!workspaceId || !urlPageId) {
    return <Navigate to={ROUTES.BASE.NOT_FOUND} />
  } else if (!page) {
    return <Loading />
  }

  const contextProps: AddEditPageContextType = {
    page,
    ...commonContextProps,
  }

  const canUpdate: boolean = ability.can('update', 'Page')

  const activeAbTestId: AbTest['id'] | undefined = page.domain.activeAbTest?.id

  return (
    <AddEditPageContext.Provider value={contextProps}>
      <StickyBox>
        <AddEditPageContext.Consumer>
          {(ctxProps: AddEditPageContextType): React.JSX.Element => (
            <>
              <PageHeader
                actions={
                  <SaveButton
                    can={canUpdate}
                    {...ctxProps}
                    {...(activeAbTestId && { onConfirmation: confirmationHandler })}
                  />
                }
                backTo={generatePath(ROUTES.INVENTORY.PAGE.LIST, { workspaceId })}
                title={page.name}
                titleAdornment={{ end: <DomainAlerts alerts={freshPageDomainAlerts} /> }}
              />

              {activeAbTestId && (
                <ConfirmationDialog
                  activeAbTestId={activeAbTestId}
                  formProps={commonContextProps}
                  isOpen={isDialogOpen}
                  setOpen={setIsDialogOpen}
                />
              )}
            </>
          )}
        </AddEditPageContext.Consumer>

        <Tabs
          path={ROUTES.INVENTORY.PAGE.EDIT}
          productFruitsSelector={generateSelector({ step: 2, tour: 2 })}
          tabs={tabs}
        />
      </StickyBox>

      {getPage<ProductTab>(tabs, urlCurrentTab, 'general')}
    </AddEditPageContext.Provider>
  )
}
